import { useMemo } from "react";
import moment from "moment";
import { Loader } from "@/components/Loader";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useGetBudgetLog } from "@/services";
import { changeText, toLogEntries } from "./logEntries";

interface BudgetLogSheetProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  communityId: string;
  budget: any;
}

const BudgetLogSheet = ({ open, setOpen, communityId, budget }: BudgetLogSheetProps) => {
  const budgetId = budget?.budgetId;
  const { data, isLoading } = useGetBudgetLog({
    communityId,
    budgetId,
    queryParams: { enabled: open && !!communityId && !!budgetId },
  });
  const entries = useMemo(() => toLogEntries(data?.data ?? []), [data]);
  // The log spans every version of a budget; say which one an entry belongs to only when there are several
  const hasVersions = useMemo(() => new Set(entries.map((entry) => entry.budgetId)).size > 1, [entries]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:!max-w-[460px] bg-white gap-0">
        <SheetHeader className="border-b border-gray-200">
          <SheetTitle className="text-[18px]">Budget Log</SheetTitle>
          <SheetDescription>
            {budget?.budgetName} · FY {budget?.year} · Version {budget?.versionNo} · ID {budgetId}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? <Loader /> : !entries.length ?
            <p className="text-[14px] text-gray-500">No activity has been recorded for this budget yet.</p> :
            <ol>
              {entries.map((entry, index) => {
                const Icon = entry.meta.icon;
                return (
                  <li key={entry.commentId} className="relative flex gap-3 pb-6 last:pb-0">
                    {index < entries.length - 1 &&
                      <span className="absolute left-[15px] top-8 bottom-0 w-px bg-gray-200" aria-hidden />}
                    <span className={cn("h-8 w-8 rounded-full flex items-center justify-center shrink-0", entry.meta.className)}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1 text-[14px]">
                      <div className="font-medium flex flex-row flex-wrap items-center gap-2">
                        {entry.meta.label}
                        {hasVersions &&
                          <span className="text-[11px] font-normal text-gray-500 border border-gray-200 rounded-full px-2">
                            Budget {entry.budgetId}
                          </span>}
                      </div>
                      {entry.subject && <div className="text-gray-700">{entry.subject}</div>}
                      <div className="text-[12px] text-gray-500">
                        {entry.createdByName || "System"} · {moment(entry.createdAt).format("MM/DD/YYYY hh:mm A")}
                      </div>
                      {entry.changes.length > 0 &&
                        <dl className="mt-2 rounded-md border border-gray-100 divide-y divide-gray-100">
                          {entry.changes.map((change: any) => (
                            <div key={change.field} className="grid grid-cols-[120px_1fr] gap-2 px-2 py-1 text-[13px]">
                              <dt className="text-gray-500">{change.field}</dt>
                              <dd className="break-words">{changeText(change)}</dd>
                            </div>
                          ))}
                        </dl>}
                      {entry.text &&
                        <div className="mt-2 rounded-md bg-gray-50 border border-gray-100 p-2 whitespace-pre-wrap break-words">
                          {entry.text}
                        </div>}
                    </div>
                  </li>
                );
              })}
            </ol>}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default BudgetLogSheet;
